import React, { ChangeEventHandler, Component, CSSProperties, FocusEventHandler } from 'react';
import { TextField } from '@mui/material';

import { withKey } from '@paragon/client-tools';

function formatFloat(num: number) {
  const numberAsString = num.toFixed(2);

  if (numberAsString.endsWith('0')) {
    return numberAsString.slice(0, -1);
  }

  return numberAsString;
}

interface Props {
  readonly label: string;
  readonly value: number;
  readonly onChange: (newValue: number, oldValue: number) => void;
  readonly style?: CSSProperties;
  readonly id?: string;
  readonly helperText?: string;
  readonly disabled?: boolean;
}

interface State {
  readonly displayValue: string;
  readonly changesOccurred: boolean;
}

/**
 * An input designed for editing floats, which will not allow invalid values to be entered.
 *
 * Can accept values of arbitrary precision, but only displays to two decimal points.
 * It will only dispatch the `onChange` event when the user is finished editing the value.
 */
class FloatInput extends Component<Props, State> {
  state: State = {
    displayValue: this.props.value ? formatFloat(this.props.value) : '0.00',
    changesOccurred: false,
  };

  onInputChange: ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement> = (event) => {
    this.setState({
      changesOccurred: true,

      displayValue: event.target.value,
    });
  };

  onInputBlur: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement> = (event) => {
    if (!this.state.changesOccurred) {
      return;
    }

    const newValue = parseFloat(event.target.value);
    if (Number.isNaN(newValue) || newValue === 0) {
      this.setState({ displayValue: formatFloat(this.props.value) });
      return;
    }

    this.setState({ changesOccurred: false }, () => {
      this.props.onChange(newValue, this.props.value);
    });
  };

  render() {
    return (
      <TextField
        {...{
          ...this.props,
          value: this.state.displayValue,
        }}
        onChange={this.onInputChange}
        onBlur={this.onInputBlur}
      />
    );
  }
}

export default withKey('value', FloatInput);
